/**
 * Exponential backoff with jitter, shared by every retry loop.
 *
 * @module utils/backoff
 */

/** The shape of one backoff schedule. */
export interface BackoffSchedule {
  /** Delay before the first retry, in milliseconds. */
  baseMs: number;
  /** Longest delay between retries, in milliseconds. */
  ceilingMs: number;
}

/**
 * Delay before retry number `attempt`, counted from zero.
 *
 * Full jitter over the upper half of the window: never shorter than half the
 * exponential delay, so backoff still grows, and never longer than the cap.
 * The jitter is what stops a hundred clients dropped by one outage from all
 * retrying in the same instant and failing together again.
 *
 * @param attempt - Retries already made, from zero.
 * @param schedule - Base and ceiling.
 * @param random - Source of randomness, injectable so tests are deterministic.
 * @returns Delay in milliseconds.
 */
export function backoffDelayMs(
  attempt: number,
  schedule: BackoffSchedule,
  random: () => number = Math.random,
): number {
  const exponential = schedule.baseMs * 2 ** Math.max(0, attempt);
  const capped = Math.min(exponential, schedule.ceilingMs);
  return Math.round(capped / 2 + random() * (capped / 2));
}
